import React from 'react';
import ReactDOM from 'react-dom/client';
import { SiteRenderer, RuntimeMessage } from '../../../packages/website-engine/src/index.jsx';
import { coreBlockRegistry } from '../../../packages/website-blocks-core/src/index.jsx';
import './runtime.css';
import { buildInfo, buildInfoTitle } from './buildInfo.js';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'https://api.simpleblue.app/api/v1').replace(/\/$/, '');

window.SimpleBlueBuildInfo = buildInfo;
if (new URLSearchParams(window.location.search).get('__build') === '1') {
  const badge = document.createElement('div');
  badge.textContent = `Build ${buildInfo.buildTag}`;
  badge.title = buildInfoTitle();
  badge.style.cssText = 'position:fixed;right:12px;bottom:12px;z-index:2147483647;background:#0f172a;color:#fff;border-radius:999px;padding:6px 10px;font:700 12px system-ui,sans-serif;box-shadow:0 10px 30px rgba(15,23,42,.28)';
  document.addEventListener('DOMContentLoaded', () => document.body.appendChild(badge), { once: true });
}
console.info('[SimpleBlue build]', buildInfo);

const overrideHost = new URLSearchParams(window.location.search).get('__host')?.trim();
const configuredHost = import.meta.env.VITE_SITE_HOST?.trim();
const host = overrideHost || configuredHost || window.location.hostname;


const analyticsSessionId = getAnalyticsSessionId();

function getAnalyticsSessionId() {
  try {
    const key = 'sb_website_analytics_session';
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const next = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    sessionStorage.setItem(key, next);
    return next;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function viewportDevice(width = window.innerWidth) {
  if (width < 700) return 'mobile';
  if (width < 1100) return 'tablet';
  return 'desktop';
}

function sendAnalytics(event) {
  try {
    const payload = JSON.stringify({
      host,
      events: [{
        ...event,
        pagePath: event.pagePath || `${window.location.pathname}${window.location.search}` || '/',
        pageTitle: event.pageTitle || document.title,
        referrer: document.referrer || '',
        deviceType: viewportDevice(),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        sessionId: analyticsSessionId,
        occurredUtc: new Date().toISOString()
      }]
    });
    const url = `${API_BASE}/public/websites/analytics/events`;
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
  } catch {}
}

function analyticsForClick(anchor) {
  const href = anchor.getAttribute('href') || '';
  const block = anchor.closest?.('[data-sb-block-id]');
  const blockId = block?.getAttribute?.('data-sb-block-id') || '';
  const label = (anchor.textContent || '').trim().slice(0, 120);
  const item = anchor.closest?.('[data-sb-item-id]')?.getAttribute?.('data-sb-item-id') || '';
  const metadata = { label };
  if (anchor.hasAttribute('download')) return { eventType: 'document_download', targetUrl: href, blockId, itemId: item, metadata };
  if (anchor.closest?.('.sb-quick-notice')) return { eventType: 'quick_notice_click', targetUrl: href, blockId, itemId: item, metadata };
  if (anchor.closest?.('nav')) return { eventType: 'nav_click', targetUrl: href, blockId, itemId: item, metadata };
  if (href.startsWith('#')) return { eventType: 'section_link_click', targetUrl: href, blockId, itemId: item, metadata };
  if (/^https?:\/\//i.test(href) && !href.includes(window.location.host)) return { eventType: 'outbound_click', targetUrl: href, blockId, itemId: item, metadata };
  if (anchor.classList.contains('sb-button') || anchor.classList.contains('sb-text-button')) return { eventType: 'cta_click', targetUrl: href, blockId, itemId: item, metadata };
  return null;
}

function applyMetadata(snapshot, page) {
  const title = page?.seo?.title || page?.title || snapshot?.site?.name || 'Website';
  document.title = title;
  const description = page?.seo?.description || snapshot?.site?.description || '';
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
  meta.content = description;
  document.documentElement.lang = snapshot?.site?.defaultLocale || 'en-US';
}

let resolvedSiteCache = null;
let resolvedSitePromise = null;

function loadQuickNotice() {
  return fetch(`${API_BASE}/public/websites/quick-notice?host=${encodeURIComponent(host)}`, { cache: 'no-store' })
    .then((response) => response.ok ? response.json() : null)
    .catch(() => null);
}

function loadResolvedSite() {
  if (resolvedSiteCache) return Promise.resolve(resolvedSiteCache);
  if (!resolvedSitePromise) {
    resolvedSitePromise = fetch(`${API_BASE}/public/websites/resolve?host=${encodeURIComponent(host)}`)
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || 'This website could not be loaded.');
        }
        return response.json();
      })
      .then((data) => {
        resolvedSiteCache = data;
        return data;
      })
      .catch((error) => {
        resolvedSitePromise = null;
        throw error;
      });
  }
  return resolvedSitePromise;
}

function App() {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const [path, setPath] = React.useState(window.location.pathname || '/');
  const lastTrackedPath = React.useRef('');

  React.useEffect(() => {
    let active = true;
    Promise.all([loadResolvedSite(), loadQuickNotice()])
      .then(([data, quickNotice]) => { if (active) setState({ loading: false, data: { ...data, quickNotice }, error: null }); })
      .catch((error) => {
        if (!active || error?.name === 'AbortError') return;
        setState({ loading: false, data: null, error: error.message });
      });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    const onPop = () => setPath(window.location.pathname || '/');
    const onClick = (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http')) return;

      if (href.startsWith('#')) {
        const target = document.querySelector(href);
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          history.replaceState(history.state, '', `${window.location.pathname}${href}`);
        }
        return;
      }

      if (href.startsWith('/')) {
        event.preventDefault();
        history.pushState({}, '', href);
        setPath(window.location.pathname || '/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    window.addEventListener('popstate', onPop);
    document.addEventListener('click', onClick);
    return () => { window.removeEventListener('popstate', onPop); document.removeEventListener('click', onClick); };
  }, []);



  React.useEffect(() => {
    if (!state.data?.snapshot) return;
    const page = state.data.snapshot.pages?.find((candidate) => (candidate.slug || '/') === path) || state.data.snapshot.pages?.find((candidate) => candidate.slug === '/');
    if (lastTrackedPath.current !== path) {
      lastTrackedPath.current = path;
      window.setTimeout(() => sendAnalytics({ eventType: 'page_view', pagePath: path, pageTitle: page?.seo?.title || page?.title || document.title }), 0);
    }
    const hasForm = page?.blocks?.some((block) => block?.type === 'form.contact@1');
    if (hasForm) window.setTimeout(() => sendAnalytics({ eventType: 'form_view', pagePath: path }), 50);
  }, [state.data, path]);

  React.useEffect(() => {
    const onAnalyticsClick = (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;
      const payload = analyticsForClick(anchor);
      if (payload) sendAnalytics(payload);
    };
    const onAnalyticsSubmit = (event) => {
      if (event.target?.matches?.('form.sb-form')) {
        const block = event.target.closest?.('[data-sb-block-id]');
        sendAnalytics({ eventType: 'form_submit', blockId: block?.getAttribute?.('data-sb-block-id') || '' });
      }
    };
    document.addEventListener('click', onAnalyticsClick, true);
    document.addEventListener('submit', onAnalyticsSubmit, true);
    return () => { document.removeEventListener('click', onAnalyticsClick, true); document.removeEventListener('submit', onAnalyticsSubmit, true); };
  }, []);

  if (state.loading) return <RuntimeMessage title="Loading website" message="Preparing this site…" />;
  if (state.error) return <RuntimeMessage title="Website unavailable" message={state.error} />;
  const snapshot = { ...state.data.snapshot, quickNotice: state.data.quickNotice };
  const page = snapshot.pages?.find((candidate) => (candidate.slug || '/') === path) || snapshot.pages?.find((candidate) => candidate.slug === '/');
  applyMetadata(snapshot, page);
  const assetResolver = (assetId) => `${API_BASE}/public/websites/assets/${encodeURIComponent(assetId)}`;
  return <SiteRenderer snapshot={snapshot} pathname={path} registry={coreBlockRegistry} assetResolver={assetResolver} formSubmitBaseUrl={API_BASE} currentHost={host} />;
}

ReactDOM.createRoot(document.getElementById('root')).render(<React.StrictMode><App /></React.StrictMode>);
